import React, { useState, Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { getTenant, formatCurrencyAmount } from '../../../theme/tokens';
import { FormField } from '../../../shared/ui/FormField';
import { MetadataEditor, MetadataItem } from '../../../shared/ui/MetadataEditor';
import { CaptureFieldsEditor, CaptureField } from '../../../shared/ui/CaptureFieldsEditor';
import { SettingsIcon, BuildingIcon, PaletteIcon, CreditCardIcon, UsersIcon, ScrollTextIcon, BellIcon, ShieldIcon, FormInputIcon, PercentIcon, ShoppingBagIcon, PackageIcon, InfoIcon, TagIcon, DollarSignIcon, ClockIcon, UploadIcon, PlusIcon, TrashIcon, MailIcon, CheckCircleIcon, WalletIcon, LockIcon, AlertCircleIcon, FileTextIcon, MoreHorizontalIcon, ActivityIcon, SearchIcon, DownloadIcon, RefreshCwIcon, ChevronDownIcon, ChevronUpIcon, ClipboardListIcon } from 'lucide-react';
type SettingsTab = 'company' | 'branding' | 'users' | 'payout' | 'general' | 'payment-providers' | 'receipt' | 'fields' | 'notifications' | 'security' | 'subscription-plan';
interface TabConfig {
  id: SettingsTab;
  label: string;
  icon: any;
  category: 'account' | 'payment' | 'ecommerce' | 'system';
}
interface ActivityLog {
  id: string;
  timestamp: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
  action: string;
  section: string;
  description: string;
  objectId: string;
  details: {
    amount?: string;
  };
  ipAddress: string;
  userAgent: string;
  status: 'success' | 'warning' | 'error' | 'info';
}
export function Settings() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<SettingsTab>('company');
  const currency = getTenant().currency.code;
  // ---- State ----
  const [companyInfo, setCompanyInfo] = useState({
    merchantId: 'MBNK-MY-001234',
    businessName: 'Bila Bila Mart',
    registrationNumber: '202301234567',
    gstNumber: 'MY123456789',
    businessEmail: 'billing@bilabilamart.mv',
    businessPhone: '+60 3-1234 5678',
    language: 'en-US'
  });
  const [brandingSettings, setBrandingSettings] = useState({
    logoUrl: getTenant().brand.logo?.light || '',
    primaryColor: '#E2231A',
    accentColor: '#2563eb'
  });
  const [generalSettings, setGeneralSettings] = useState({
    defaultExpiryDays: 7,
    redirectUrl: 'https://bilabilamart.mv/payment-success',
    enableAutoReminders: true
  });
  const [receiptSettings, setReceiptSettings] = useState({
    showCompanyAddress: true,
    companyAddress: 'H. Maaveyo, Boduthakurufaanu Magu\nMalé 20094\nMaldives',
    ccEmail: 'receipts@bilabilamart.mv',
    footerText: 'Thank you for your business!'
  });
  const [paymentMethods, setPaymentMethods] = useState([{
    id: 'visa',
    name: 'Visa',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'mastercard',
    name: 'Mastercard',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'amex',
    name: 'American Express',
    enabled: true,
    icon: CreditCardIcon,
    category: 'card'
  }, {
    id: 'fpx',
    name: 'FPX (Online Banking)',
    enabled: true,
    icon: BuildingIcon,
    category: 'banking'
  }, {
    id: 'tng',
    name: "Touch 'n Go eWallet",
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'grabpay',
    name: 'GrabPay',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'duitnow',
    name: 'DuitNow QR',
    enabled: true,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'boost',
    name: 'Boost',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }, {
    id: 'shopeepay',
    name: 'ShopeePay',
    enabled: false,
    icon: WalletIcon,
    category: 'ewallet'
  }]);
  const [users, setUsers] = useState([{
    id: '1',
    name: 'Ahmed Lee',
    email: 'ahmed@bilabilamart.mv',
    role: 'Owner',
    status: 'Active'
  }, {
    id: '2',
    name: 'Aishath Tan',
    email: 'aishath@bilabilamart.mv',
    role: 'Finance',
    status: 'Active'
  }, {
    id: '3',
    name: 'Ibrahim Rahman',
    email: 'ibrahim@bilabilamart.mv',
    role: 'Operations',
    status: 'Active'
  }]);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [showAllInvitePerms, setShowAllInvitePerms] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('Operations');
  const [showRoleManagementModal, setShowRoleManagementModal] = useState(false);
  const [openUserMenuId, setOpenUserMenuId] = useState<string | null>(null);
  const [editingUser, setEditingUser] = useState<{
    id: string;
    name: string;
    email: string;
    role: string;
  } | null>(null);
  // Role definitions with permissions
  const rolePermissions = {
    Owner: {
      description: 'Full access to all features and settings',
      permissions: {
        Dashboard: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Payments: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Users: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Reports: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        }
      }
    },
    'Merchant Management': {
      description: 'Create/edit/view merchant accounts, KYC, onboarding',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    'Account Management': {
      description: 'View and manage existing merchant accounts, support actions, no financial controls',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Sales: {
      description: 'View merchant pipeline, create leads, view commercial terms, no settlement/finance access',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: true,
          modify: false,
          insert: true,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Bills: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Balance: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Finance: {
      description: 'View settlements, payouts, MDR, invoices, refunds; no merchant onboarding rights',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Customers: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Bills: {
          read: true,
          write: true,
          modify: true,
          insert: true,
          approve: true
        },
        Balance: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: true
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: true,
          modify: false,
          insert: false,
          approve: false
        }
      }
    },
    Operations: {
      description: 'Transaction monitoring, dispute handling, reconciliation, basic merchant edits',
      permissions: {
        Dashboard: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Payments: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Refunds: {
          read: true,
          write: true,
          modify: true,
          insert: false,
          approve: false
        },
        Customers: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Invoices: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Bills: {
          read: true,
          write: false,
          modify: true,
          insert: false,
          approve: false
        },
        Balance: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Settings: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Users: {
          read: false,
          write: false,
          modify: false,
          insert: false,
          approve: false
        },
        Reports: {
          read: true,
          write: false,
          modify: false,
          insert: false,
          approve: false
        }
      }
    }
  };
  const [termsSettings, setTermsSettings] = useState({
    termsText: 'By using our payment services, you agree to our terms and conditions.',
    privacyPolicy: 'We collect and process payment information in accordance with Maldivian data protection regulations.'
  });
  const [presetMetadataFields, setPresetMetadataFields] = useState<MetadataItem[]>([{
    key: 'customer_email',
    value: '',
    isPreset: true,
    required: true,
    type: 'email',
    description: "Customer's email address for receipts"
  }, {
    key: 'invoice_number',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Reference invoice number'
  }, {
    key: 'order_id',
    value: '',
    isPreset: true,
    required: false,
    type: 'text',
    description: 'Internal order ID'
  }]);
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: true,
    smsNotifications: false,
    paymentReceived: true,
    paymentFailed: true,
    refundProcessed: true,
    disputeOpened: true,
    settlementCompleted: true,
    lowBalance: true
  });
  const [securitySettings, setSecuritySettings] = useState({
    twoFactorEnabled: true,
    sessionTimeout: 30,
    ipWhitelist: '',
    apiKeyVisible: false
  });
  const [selectedShop, setSelectedShop] = useState('Beach Shop');
  const [shopSettings, setShopSettings] = useState({
    inventoryTracking: true,
    lowStockThreshold: 5,
    notifyOnLowStock: false,
    allowBackorders: false,
    hideOutOfStock: false,
    enableTaxCalculation: false,
    pricesIncludeTax: false,
    processingFee: 0,
    shippingFee: 0
  });
  const [taxRates, setTaxRates] = useState([{
    id: '1',
    name: 'Standard Rate',
    rate: 6,
    isDefault: true
  }, {
    id: '2',
    name: 'Reduced Rate',
    rate: 3,
    isDefault: false
  }, {
    id: '3',
    name: 'Zero Rate',
    rate: 0,
    isDefault: false
  }]);
  const [customerFields, setCustomerFields] = useState([{
    id: '1',
    name: 'Full Name',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '2',
    name: 'Email',
    type: 'Email',
    required: true,
    enabled: true
  }, {
    id: '3',
    name: 'Phone Number',
    type: 'Phone',
    required: true,
    enabled: true
  }, {
    id: '4',
    name: 'Address',
    type: 'Text',
    required: true,
    enabled: true
  }, {
    id: '5',
    name: 'Additional Notes',
    type: 'Textarea',
    required: false,
    enabled: true
  }]);
  const [orderSettings, setOrderSettings] = useState({
    orderPrefix: 'ORD-',
    startingNumber: 1000
  });
  // Payout settings state
  const [minBalanceEnabled, setMinBalanceEnabled] = useState(false);
  const [minBalanceAmount, setMinBalanceAmount] = useState('');
  const [payoutDescriptor, setPayoutDescriptor] = useState('');
  const [shopFees, setShopFees] = useState([{
    id: '1',
    name: 'Processing Fee',
    type: 'Percentage',
    amount: 2.5,
    condition: 'Applied to all orders'
  }, {
    id: '2',
    name: 'Shipping Fee',
    type: 'Fixed',
    amount: 5.0,
    condition: 'Applied to orders over MVR 100'
  }]);
  const [shopPayoutSettings, setShopPayoutSettings] = useState({
    bankAccount: 'BML - 773000000001',
    frequency: 'Weekly',
    minimumAmount: 1000
  });
  const [shopNotifications, setShopNotifications] = useState({
    confirmationMessage: 'Thank you for your order! We will process it shortly.',
    notificationEmail: 'notifications@bilabilamart.mv',
    notificationPhone: '+60 3-1234 5678',
    sendOrderNotifications: true,
    sendLowStockAlerts: false
  });
  // Activity Logs state
  const [activityLogs] = useState(() => {
    const logs = [];
    const now = new Date();
    const actions = [{
      type: 'created',
      section: 'Payments',
      desc: 'Payment created'
    }, {
      type: 'updated',
      section: 'Payments',
      desc: 'Payment updated'
    }, {
      type: 'deleted',
      section: 'Payments',
      desc: 'Payment deleted'
    }, {
      type: 'created',
      section: 'Customers',
      desc: 'Customer created'
    }, {
      type: 'updated',
      section: 'Customers',
      desc: 'Customer updated'
    }, {
      type: 'deleted',
      section: 'Customers',
      desc: 'Customer deleted'
    }, {
      type: 'created',
      section: 'Invoices',
      desc: 'Invoice created'
    }, {
      type: 'updated',
      section: 'Invoices',
      desc: 'Invoice updated'
    }, {
      type: 'approved',
      section: 'Bills',
      desc: 'Bill approved'
    }, {
      type: 'created',
      section: 'Refunds',
      desc: 'Refund created'
    }, {
      type: 'updated',
      section: 'Settings',
      desc: 'Settings updated'
    }, {
      type: 'created',
      section: 'Users',
      desc: 'User invited'
    }, {
      type: 'updated',
      section: 'Security',
      desc: 'Security settings updated'
    }];
    const realUsers = [{
      id: '1',
      name: 'Ahmed Lee',
      email: 'ahmed@bilabilamart.mv',
      role: 'Owner'
    }, {
      id: '2',
      name: 'Aishath Tan',
      email: 'aishath@bilabilamart.mv',
      role: 'Finance'
    }, {
      id: '3',
      name: 'Ibrahim Rahman',
      email: 'ibrahim@bilabilamart.mv',
      role: 'Operations'
    }];
    for (let i = 0; i < 80; i++) {
      const action = actions[Math.floor(Math.random() * actions.length)];
      const user = realUsers[Math.floor(Math.random() * realUsers.length)];
      const minutesAgo = Math.floor(Math.random() * 43200); // 30 days in minutes
      const timestamp = new Date(now.getTime() - minutesAgo * 60000);
      logs.push({
        id: `log-${i + 1}`,
        timestamp: timestamp.toISOString(),
        user: {
          id: user.id,
          name: user.name,
          email: user.email,
          role: user.role
        },
        action: action.type,
        section: action.section,
        description: action.desc,
        objectId: `${action.section.toLowerCase()}_${Math.random().toString(36).substr(2, 9)}`,
        details: {
          amount: Math.random() > 0.5 ? `MVR ${(Math.random() * 1000).toFixed(2)}` : undefined
        },
        ipAddress: `${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}.${Math.floor(Math.random() * 255)}`,
        userAgent: 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/58.0.3029.110 Safari/537.3.'
      });
    }
    return logs.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());
  });
  const [logFilters, setLogFilters] = useState({
    search: '',
    dateRange: '7days',
    user: 'all',
    actionType: 'all',
    section: 'all'
  });
  const [logsPerPage, setLogsPerPage] = useState(25);
  const [currentLogPage, setCurrentLogPage] = useState(1);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);
  // Merchant Requests state
  const [merchantRequests] = useState(() => {
    const now = new Date();
    return [{
      id: 'REQ-001',
      type: 'Payment Method Activation',
      description: 'Request to activate ShopeePay payment method',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 2 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        paymentMethod: 'ShopeePay',
        reason: 'Customer demand for ShopeePay payments'
      }
    }, {
      id: 'REQ-002',
      type: 'User Role Request',
      description: 'Request for custom "Customer Support" role',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 5 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        roleName: 'Customer Support',
        permissions: 'Read-only access to payments and customers'
      }
    }, {
      id: 'REQ-003',
      type: 'Payment Method Activation',
      description: 'Request to activate Boost eWallet',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'Boost',
        reason: 'Expand payment options for customers',
        rejectionReason: 'Merchant category not supported by Boost'
      }
    }, {
      id: 'REQ-004',
      type: 'Payment Method Activation',
      description: 'Request to activate American Express',
      requestedBy: 'Ahmed Lee',
      requestedAt: new Date(now.getTime() - 10 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'In Progress',
      details: {
        paymentMethod: 'American Express',
        reason: 'Corporate clients prefer Amex'
      }
    }, {
      id: 'REQ-005',
      type: 'User Role Request',
      description: 'Request for "Marketing" role with limited access',
      requestedBy: 'Aishath Tan',
      requestedAt: new Date(now.getTime() - 12 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Pending',
      details: {
        roleName: 'Marketing',
        permissions: 'Read access to reports and customer data'
      }
    }, {
      id: 'REQ-006',
      type: 'Payment Method Activation',
      description: 'Request to activate DuitNow QR',
      requestedBy: 'Ibrahim Rahman',
      requestedAt: new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000).toISOString(),
      status: 'Closed',
      details: {
        paymentMethod: 'DuitNow QR',
        reason: 'Low-cost payment option for customers'
      }
    }].sort((a, b) => new Date(b.requestedAt).getTime() - new Date(a.requestedAt).getTime());
  });
  const tabs: TabConfig[] = [
  // ACCOUNT
  {
    id: 'company',
    label: 'Company',
    icon: BuildingIcon,
    category: 'account'
  }, {
    id: 'branding',
    label: 'Branding',
    icon: PaletteIcon,
    category: 'account'
  }, {
    id: 'users',
    label: 'Manage Users',
    icon: UsersIcon,
    category: 'account'
  }, {
    id: 'payout',
    label: 'Payout',
    icon: DollarSignIcon,
    category: 'account'
  },
  // PAYMENT SETTINGS
  {
    id: 'general',
    label: 'General',
    icon: SettingsIcon,
    category: 'payment'
  }, {
    id: 'payment-providers',
    label: 'Payment Providers',
    icon: CreditCardIcon,
    category: 'payment'
  }, {
    id: 'receipt',
    label: 'Receipt Information',
    icon: MailIcon,
    category: 'payment'
  }, {
    id: 'fields',
    label: 'Information Fields',
    icon: FormInputIcon,
    category: 'payment'
  }, {
    id: 'notifications',
    label: 'Notifications',
    icon: BellIcon,
    category: 'payment'
  }, {
    id: 'security',
    label: 'Security',
    icon: ShieldIcon,
    category: 'payment'
  },
  // E-COMMERCE
  {
    id: 'subscription-plan',
    label: 'Subscription Plan',
    icon: CreditCardIcon,
    category: 'account'
  }];
  const categories = [{
    id: 'account',
    label: 'ACCOUNT'
  }, {
    id: 'payment',
    label: 'PAYMENT SETTINGS'
  }];
  const SaveButton = () => <div className="pt-4" data-id="element-9473">
      <button className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-9474">
        Save Changes
      </button>
    </div>;
  // ---- Render functions ----
  const renderCompanySettings = () => <div className="space-y-6" data-id="element-9475">
      <div data-id="element-9476">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9477">
          Company Information
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9478">
          Manage your business details and merchant account information
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9479">
        <div className="flex items-start gap-3" data-id="element-9480">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9481" />
          <div className="text-sm text-gray-700" data-id="element-9482">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9483">
              Verified Merchant Account
            </p>
            <p className="text-xs" data-id="element-9484">
              Your merchant account has been verified and is active. Contact
              support if you need to update your business registration details.
            </p>
          </div>
        </div>
      </div>

      {/* Company Details Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9485">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9486">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9487">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-9488" />
            Business Details
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9489">
          <FormField label="Merchant ID" tooltip="Your unique merchant identifier" data-id="element-9490">
            <div className="flex items-center gap-2" data-id="element-9491">
              <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-50" value={companyInfo.merchantId} readOnly data-id="element-9492" />
              <span className="inline-flex items-center px-3 py-1.5 rounded-md text-xs font-semibold bg-green-100 text-green-800 whitespace-nowrap" data-id="element-9493">
                <CheckCircleIcon size={14} className="mr-1" data-id="element-9494" />
                VERIFIED
              </span>
            </div>
          </FormField>

          <FormField label="Business Name" required data-id="element-9495">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessName} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessName: e.target.value
          })} data-id="element-9496" />
          </FormField>

          <FormField label="Registration Number" required data-id="element-9497">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.registrationNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            registrationNumber: e.target.value
          })} data-id="element-9498" />
          </FormField>

          <FormField label="GST/SST Number" data-id="element-9499">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.gstNumber} onChange={e => setCompanyInfo({
            ...companyInfo,
            gstNumber: e.target.value
          })} data-id="element-9500" />
          </FormField>

          <FormField label="Business Email" required data-id="element-9501">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessEmail} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessEmail: e.target.value
          })} data-id="element-9502" />
          </FormField>

          <FormField label="Business Phone" required data-id="element-9503">
            <input type="tel" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={companyInfo.businessPhone} onChange={e => setCompanyInfo({
            ...companyInfo,
            businessPhone: e.target.value
          })} data-id="element-9504" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9505" />
    </div>;
  const renderBrandingSettings = () => <div className="space-y-6" data-id="element-9506">
      <div data-id="element-9507">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9508">Branding</h2>
        <p className="text-sm text-gray-600" data-id="element-9509">
          Customize your brand appearance across payment pages and receipts
        </p>
      </div>

      {/* Logo Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9510">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9511">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9512">
            <PaletteIcon size={18} className="text-gray-600" data-id="element-9513" />
            Logo & Visual Identity
          </h3>
        </div>
        <div className="p-6 space-y-6" data-id="element-9514">
          <FormField label="Company Logo" description="Upload your company logo (recommended: 200x60px, PNG or SVG)." data-id="element-9515">
            <div className="flex items-center gap-4" data-id="element-9516">
              {brandingSettings.logoUrl && <div className="border border-gray-200 rounded-lg p-4 bg-white" data-id="element-9517">
                  <img src={brandingSettings.logoUrl} alt="Company Logo" className="h-12 object-contain" data-id="element-9518" />
                </div>}
              <button className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center transition-colors" data-id="element-9519">
                <UploadIcon size={16} className="mr-2" data-id="element-9520" />
                Upload New Logo
              </button>
            </div>
          </FormField>

          <FormField label="Primary Brand Color" description="This color will be used for buttons and accents" data-id="element-9521">
            <div className="flex items-center gap-3" data-id="element-9522">
              <input type="color" className="h-10 w-20 border border-gray-300 rounded cursor-pointer" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-9523" />
              <input type="text" className="block w-32 px-3 py-2 border border-gray-300 rounded-md font-mono text-sm" value={brandingSettings.primaryColor} onChange={e => setBrandingSettings({
              ...brandingSettings,
              primaryColor: e.target.value
            })} data-id="element-9524" />
              <div className="flex-1 text-sm text-gray-600" data-id="element-9525">
                Current: BML Red
              </div>
            </div>
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9526" />
    </div>;
  const renderGeneralSettings = () => <div className="space-y-6" data-id="element-9527">
      <div data-id="element-9528">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9529">
          General Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9530">
          Configure default behaviors and preferences for your payment system
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9531">
        <div className="flex items-start gap-3" data-id="element-9532">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9533" />
          <div className="text-sm text-gray-700" data-id="element-9534">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9535">
              Payment Link Defaults
            </p>
            <p className="text-xs" data-id="element-9536">
              These settings apply to all new payment links and invoices. You
              can override them individually when creating each payment.
            </p>
          </div>
        </div>
      </div>

      {/* Settings Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9537">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9538">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9539">
            <SettingsIcon size={18} className="text-gray-600" data-id="element-9540" />
            Default Configuration
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9541">
          <FormField label="Default Expiry Days" description="Number of days before payment links expire" data-id="element-9542">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={generalSettings.defaultExpiryDays} onChange={e => setGeneralSettings({
            ...generalSettings,
            defaultExpiryDays: parseInt(e.target.value)
          })} data-id="element-9543" />
          </FormField>

          <FormField label="Redirect URL" description="Where customers are sent after successful payment" data-id="element-9544">
            <input type="url" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-mono text-sm" value={generalSettings.redirectUrl} onChange={e => setGeneralSettings({
            ...generalSettings,
            redirectUrl: e.target.value
          })} data-id="element-9545" />
          </FormField>

          <div className="pt-2" data-id="element-9546">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-9547">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={generalSettings.enableAutoReminders} onChange={e => setGeneralSettings({
              ...generalSettings,
              enableAutoReminders: e.target.checked
            })} data-id="element-9548" />
              <div data-id="element-9549">
                <p className="text-sm font-medium text-gray-900" data-id="element-9550">
                  Enable automatic payment reminders
                </p>
                <p className="text-xs text-gray-500" data-id="element-9551">
                  Send reminder emails for unpaid invoices
                </p>
              </div>
            </label>
          </div>
        </div>
      </div>

      <SaveButton data-id="element-9552" />
    </div>;
  const renderReceiptSettings = () => <div className="space-y-6" data-id="element-9553">
      <div data-id="element-9554">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9555">
          Receipt Settings
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9556">
          Customize the receipts sent to your customers after payment
        </p>
      </div>

      {/* Receipt Customization Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9557">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9558">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9559">
            <MailIcon size={18} className="text-gray-600" data-id="element-9560" />
            Receipt Customization
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9561">
          <div className="pb-2" data-id="element-9562">
            <label className="flex items-center gap-3 cursor-pointer" data-id="element-9563">
              <input type="checkbox" className="h-4 w-4 rounded border-gray-300 text-[#E2231A] focus:ring-[#E2231A]" checked={receiptSettings.showCompanyAddress} onChange={e => setReceiptSettings({
              ...receiptSettings,
              showCompanyAddress: e.target.checked
            })} data-id="element-9564" />
              <div data-id="element-9565">
                <p className="text-sm font-medium text-gray-900" data-id="element-9566">
                  Show company address on receipts
                </p>
                <p className="text-xs text-gray-500" data-id="element-9567">
                  Display your business address on customer receipts
                </p>
              </div>
            </label>
          </div>

          <FormField label="Company Address" description="This address will appear on receipts if enabled above" data-id="element-9568">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={3} value={receiptSettings.companyAddress} onChange={e => setReceiptSettings({
            ...receiptSettings,
            companyAddress: e.target.value
          })} data-id="element-9569" />
          </FormField>

          <FormField label="CC Email" description="Automatically CC this email on all receipts" data-id="element-9570">
            <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.ccEmail} onChange={e => setReceiptSettings({
            ...receiptSettings,
            ccEmail: e.target.value
          })} data-id="element-9571" />
          </FormField>

          <FormField label="Footer Text" description="Custom message at the bottom of receipts" data-id="element-9572">
            <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={receiptSettings.footerText} onChange={e => setReceiptSettings({
            ...receiptSettings,
            footerText: e.target.value
          })} data-id="element-9573" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9574" />
    </div>;
  const renderPaymentProviders = () => <div className="space-y-6" data-id="element-9575">
      <div data-id="element-9576">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9577">
          Payment Providers
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9578">
          Enable or disable payment methods available to your customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-9579">
        <div className="flex items-start gap-3" data-id="element-9580">
          <AlertCircleIcon size={20} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-9581" />
          <div className="text-sm text-gray-700" data-id="element-9582">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9583">
              Payment Method Availability
            </p>
            <p className="text-xs" data-id="element-9584">
              Disabling a payment method will hide it from all payment pages.
              Customers won't be able to use disabled methods for new payments.
            </p>
          </div>
        </div>
      </div>

      {/* Card Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9585">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9586">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9587">
            <CreditCardIcon size={18} className="text-gray-600" data-id="element-9588" />
            Card Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9589">
          {paymentMethods.filter(m => m.category === 'card').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9590">
                  <div className="flex items-center gap-3" data-id="element-9591">
                    <Icon size={20} className="text-gray-600" data-id="element-9592" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9593">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9594">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9595" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9596"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* E-Wallet Payment Methods */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9597">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9598">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9599">
            <WalletIcon size={18} className="text-gray-600" data-id="element-9600" />
            E-Wallets & QR Payments
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9601">
          {paymentMethods.filter(m => m.category === 'ewallet').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9602">
                  <div className="flex items-center gap-3" data-id="element-9603">
                    <Icon size={20} className="text-gray-600" data-id="element-9604" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9605">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9606">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9607" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9608"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>

      {/* Online Banking */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9609">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9610">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9611">
            <BuildingIcon size={18} className="text-gray-600" data-id="element-9612" />
            Online Banking
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9613">
          {paymentMethods.filter(m => m.category === 'banking').map(method => {
          const Icon = method.icon;
          return <div key={method.id} className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9614">
                  <div className="flex items-center gap-3" data-id="element-9615">
                    <Icon size={20} className="text-gray-600" data-id="element-9616" />
                    <span className="text-sm font-medium text-gray-900" data-id="element-9617">
                      {method.name}
                    </span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer" data-id="element-9618">
                    <input type="checkbox" className="sr-only peer" checked={method.enabled} onChange={e => setPaymentMethods(paymentMethods.map(m => m.id === method.id ? {
                ...m,
                enabled: e.target.checked
              } : m))} data-id="element-9619" />
                    <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9620"></div>
                  </label>
                </div>;
        })}
        </div>
      </div>
    </div>;
  const renderUsers = () => <div className="space-y-6" data-id="element-9621">
      <div className="flex items-center justify-between" data-id="element-9622">
        <div data-id="element-9623">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9624">
            Users & Permissions
          </h2>
          <p className="text-sm text-gray-600" data-id="element-9625">
            Manage team members and their access levels
          </p>
        </div>
        <div className="flex gap-3" data-id="element-9626">
          <button onClick={() => setShowRoleManagementModal(true)} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md flex items-center hover:bg-gray-50 transition-colors" data-id="element-9627">
            <ShieldIcon size={16} className="mr-2" data-id="element-9628" />
            Permissions
          </button>
          <button onClick={() => setShowInviteForm(!showInviteForm)} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md flex items-center hover:bg-[#EB4A42] transition-colors" data-id="element-9629">
            <PlusIcon size={16} className="mr-2" data-id="element-9630" />
            Invite User
          </button>
        </div>
      </div>

      {/* Invite Form */}
      {showInviteForm && <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9631">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9632">
            <h3 className="text-md font-semibold text-gray-900" data-id="element-9633">
              Invite New User
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-9634">
            <FormField label="Email Address" required data-id="element-9635">
              <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="user@example.com" data-id="element-9636" />
            </FormField>
            <FormField label="Role" required data-id="element-9637">
              <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={inviteRole} onChange={e => setInviteRole(e.target.value)} data-id="element-9638">
                <option value="Owner" data-id="element-9639">Owner</option>
                <option value="Merchant Management" data-id="element-9640">Merchant Management</option>
                <option value="Account Management" data-id="element-9641">Account Management</option>
                <option value="Sales" data-id="element-9642">Sales</option>
                <option value="Finance" data-id="element-9643">Finance</option>
                <option value="Operations" data-id="element-9644">Operations</option>
              </select>
              {inviteRole && rolePermissions[inviteRole as keyof typeof rolePermissions] && <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md" data-id="element-9645">
                    <p className="text-sm font-medium text-gray-900 mb-1" data-id="element-9646">
                      {inviteRole}
                    </p>
                    <p className="text-xs text-gray-600" data-id="element-9647">
                      {rolePermissions[inviteRole as keyof typeof rolePermissions].description}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-2" data-id="element-9648">
                      {Object.entries(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).slice(0, showAllInvitePerms ? Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length : 5).map(([section, perms]) => {
                const activePerms = Object.entries(perms).filter(([_, val]) => val).map(([key]) => key.charAt(0).toUpperCase() + key.slice(1));
                if (activePerms.length === 0) return null;
                return <span key={section} className="text-xs px-2 py-1 bg-white border border-blue-200 rounded text-gray-700" data-id="element-9649">
                              {section}: {activePerms.join(', ')}
                            </span>;
              })}
                      {Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length > 5 && <button type="button" onClick={() => setShowAllInvitePerms(!showAllInvitePerms)} className="text-xs px-2 py-1 text-blue-600 hover:text-blue-700 hover:underline font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded" data-id="element-9650">
                          {showAllInvitePerms ? 'Show less' : `+${Object.keys(rolePermissions[inviteRole as keyof typeof rolePermissions].permissions).length - 5} more`}
                        </button>}
                    </div>
                  </div>}
            </FormField>
            <div className="flex gap-3 pt-2" data-id="element-9651">
              <button onClick={() => {
            if (inviteEmail && inviteRole) {
              const newUser = {
                id: `user-${Date.now()}`,
                name: inviteEmail.split('@')[0],
                email: inviteEmail,
                role: inviteRole,
                status: 'Invited' as const
              };
              setUsers([...users, newUser]);
              setInviteEmail('');
              setInviteRole('Manager');
              setShowInviteForm(false);
            }
          }} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-9652">
                Send Invite
              </button>
              <button onClick={() => {
            setShowInviteForm(false);
            setInviteEmail('');
            setInviteRole('Operations');
          }} className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-md hover:bg-gray-50 transition-colors" data-id="element-9653">
                Cancel
              </button>
            </div>
          </div>
        </div>}

      {/* Users Table */}
      <div className="border border-gray-200 rounded-lg" data-id="element-9654">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200 rounded-t-lg" data-id="element-9655">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9656">
            <UsersIcon size={18} className="text-gray-600" data-id="element-9657" />
            Team Members ({users.length})
          </h3>
        </div>
        <div data-id="element-9658">
          <table className="w-full" data-id="element-9659">
            <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9660">
              <tr data-id="element-9661">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9662">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9663">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9664">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9665">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9666">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9667">
              {users.map(u => <tr key={u.id} className="hover:bg-gray-50 transition-colors" data-id="element-9668">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900" data-id="element-9669">
                    {u.name}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-9670">{u.email}</td>
                  <td className="px-6 py-4 text-sm text-gray-600" data-id="element-9671">{u.role}</td>
                  <td className="px-6 py-4" data-id="element-9672">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${u.status === 'Active' ? 'bg-green-100 text-green-800' : u.status === 'Invited' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-9673">
                      {u.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm" data-id="element-9674">
                    <div className="flex items-center justify-end gap-2" data-id="element-9675">
                      {u.role === 'Owner' ? <span className="text-gray-400 text-xs" data-id="element-9676">Owner</span> : <div className="relative inline-block" data-id="element-9677">
                          <button type="button" aria-label={`Actions for ${u.name}`} aria-haspopup="menu" aria-expanded={openUserMenuId === u.id} onClick={() => setOpenUserMenuId(openUserMenuId === u.id ? null : u.id)} className="p-1.5 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-9678">
                            <MoreHorizontalIcon size={18} data-id="element-9679" />
                          </button>
                          {openUserMenuId === u.id && <>
                              <div className="fixed inset-0 z-10" onClick={() => setOpenUserMenuId(null)} data-id="element-9680" />
                              <div role="menu" className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-20" data-id="element-9681">
                                {u.status !== 'Invited' && <button role="menuitem" onClick={() => {
                          setEditingUser({
                            id: u.id,
                            name: u.name,
                            email: u.email,
                            role: u.role
                          });
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50" data-id="element-9682">
                                    Edit
                                  </button>}
                                {u.status === 'Active' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Disabled' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-amber-700 hover:bg-amber-50" data-id="element-9683">
                                    Disable
                                  </button>}
                                {u.status === 'Disabled' && <button role="menuitem" onClick={() => {
                          setUsers(users.map(user => user.id === u.id ? {
                            ...user,
                            status: 'Active' as const
                          } : user));
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-green-700 hover:bg-green-50" data-id="element-9684">
                                    Enable
                                  </button>}
                                <button role="menuitem" onClick={() => {
                          if (confirm(u.status === 'Invited' ? `Cancel invite for ${u.email}?` : `Are you sure you want to delete ${u.name}?`)) {
                            setUsers(users.filter(user => user.id !== u.id));
                          }
                          setOpenUserMenuId(null);
                        }} className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-9685">
                                  {u.status === 'Invited' ? 'Cancel Invite' : 'Delete'}
                                </button>
                              </div>
                            </>}
                        </div>}
                    </div>
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit User Modal */}
      {editingUser && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-9686">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden" data-id="element-9687">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-9688">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-9689">
                Edit Team Member
              </h3>
              <button onClick={() => setEditingUser(null)} className="text-gray-400 hover:text-gray-600" aria-label="Close" data-id="element-9690">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9691">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-9692" />
                </svg>
              </button>
            </div>
            <div className="p-6 space-y-4" data-id="element-9693">
              <FormField label="Name" data-id="element-9694">
                <input type="text" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.name} onChange={e => setEditingUser({
              ...editingUser,
              name: e.target.value
            })} data-id="element-9695" />
              </FormField>
              <FormField label="Email" data-id="element-9696">
                <input type="email" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.email} onChange={e => setEditingUser({
              ...editingUser,
              email: e.target.value
            })} data-id="element-9697" />
              </FormField>
              <FormField label="Role" data-id="element-9698">
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={editingUser.role} onChange={e => setEditingUser({
              ...editingUser,
              role: e.target.value
            })} data-id="element-9699">
                  <option value="Merchant Management" data-id="element-9700">
                    Merchant Management
                  </option>
                  <option value="Account Management" data-id="element-9701">Account Management</option>
                  <option value="Sales" data-id="element-9702">Sales</option>
                  <option value="Finance" data-id="element-9703">Finance</option>
                  <option value="Operations" data-id="element-9704">Operations</option>
                </select>
              </FormField>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3 bg-gray-50" data-id="element-9705">
              <button onClick={() => setEditingUser(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-9706">
                Cancel
              </button>
              <button onClick={() => {
            setUsers(users.map(user => user.id === editingUser.id ? {
              ...user,
              name: editingUser.name,
              email: editingUser.email,
              role: editingUser.role
            } : user));
            setEditingUser(null);
          }} className="px-4 py-2 text-sm font-medium bg-[#E2231A] text-white rounded-md hover:bg-[#EB4A42]" data-id="element-9707">
                Save Changes
              </button>
            </div>
          </div>
        </div>}

      {/* Role Management Modal */}
      {showRoleManagementModal && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-9708">
          <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-id="element-9709">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between" data-id="element-9710">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-9711">
                Role Permissions Matrix
              </h3>
              <button onClick={() => setShowRoleManagementModal(false)} className="text-gray-400 hover:text-gray-600" data-id="element-9712">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-9713">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" data-id="element-9714" />
                </svg>
              </button>
            </div>
            <div className="p-6 overflow-auto flex-1" data-id="element-9715">
              <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-9716">
                {Object.entries(rolePermissions).map(([role, config]) => <div key={role} className="border border-gray-200 rounded-lg p-4" data-id="element-9717">
                    <h4 className="font-semibold text-gray-900 mb-1" data-id="element-9718">{role}</h4>
                    <p className="text-xs text-gray-600" data-id="element-9719">
                      {config.description}
                    </p>
                  </div>)}
              </div>
              <div className="overflow-x-auto" data-id="element-9720">
                <table className="w-full border border-gray-200 rounded-lg" data-id="element-9721">
                  <thead className="bg-gray-50" data-id="element-9722">
                    <tr data-id="element-9723">
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200" data-id="element-9724">
                        Section
                      </th>
                      {Object.keys(rolePermissions).map(role => <th key={role} colSpan={5} className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-l border-gray-200" data-id="element-9725">
                          {role}
                        </th>)}
                    </tr>
                    <tr data-id="element-9726">
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9727"></th>
                      {Object.keys(rolePermissions).map(role => <Fragment key={role} data-id="element-9728">
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-l border-gray-200" data-id="element-9729">
                            R
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9730">
                            W
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9731">
                            M
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9732">
                            I
                          </th>
                          <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 border-b border-gray-200" data-id="element-9733">
                            A
                          </th>
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200" data-id="element-9734">
                    {Object.keys(rolePermissions.Owner.permissions).map(section => <tr key={section} className="hover:bg-gray-50" data-id="element-9735">
                          <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-9736">
                            {section}
                          </td>
                          {Object.entries(rolePermissions).map(([role, config]) => {
                    const perms = config.permissions[section as keyof typeof config.permissions];
                    return <Fragment key={role} data-id="element-9737">
                                  <td className="px-2 py-3 text-center border-l border-gray-200" data-id="element-9738">
                                    {perms.read ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9739" /> : <span className="text-gray-300" data-id="element-9740">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9741">
                                    {perms.write ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9742" /> : <span className="text-gray-300" data-id="element-9743">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9744">
                                    {perms.modify ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9745" /> : <span className="text-gray-300" data-id="element-9746">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9747">
                                    {perms.insert ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9748" /> : <span className="text-gray-300" data-id="element-9749">—</span>}
                                  </td>
                                  <td className="px-2 py-3 text-center" data-id="element-9750">
                                    {perms.approve ? <CheckCircleIcon size={16} className="inline text-green-600" data-id="element-9751" /> : <span className="text-gray-300" data-id="element-9752">—</span>}
                                  </td>
                                </Fragment>;
                  })}
                        </tr>)}
                  </tbody>
                </table>
              </div>
              <div className="mt-4 text-xs text-gray-500" data-id="element-9753">
                <p className="font-medium mb-1" data-id="element-9754">Permission Legend:</p>
                <div className="flex flex-wrap gap-4" data-id="element-9755">
                  <span data-id="element-9756">
                    <strong data-id="element-9757">R</strong> = Read
                  </span>
                  <span data-id="element-9758">
                    <strong data-id="element-9759">W</strong> = Write
                  </span>
                  <span data-id="element-9760">
                    <strong data-id="element-9761">M</strong> = Modify
                  </span>
                  <span data-id="element-9762">
                    <strong data-id="element-9763">I</strong> = Insert
                  </span>
                  <span data-id="element-9764">
                    <strong data-id="element-9765">A</strong> = Approve
                  </span>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end" data-id="element-9766">
              <button onClick={() => setShowRoleManagementModal(false)} className="px-4 py-2 bg-[#E2231A] text-white font-medium rounded-md hover:bg-[#EB4A42] transition-colors" data-id="element-9767">
                Close
              </button>
            </div>
          </div>
        </div>}
    </div>;
  const renderTerms = () => <div className="space-y-6" data-id="element-9768">
      <div data-id="element-9769">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9770">
          Terms & Privacy
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9771">
          Configure legal terms and privacy policy shown to customers
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9772">
        <div className="flex items-start gap-3" data-id="element-9773">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9774" />
          <div className="text-sm text-gray-700" data-id="element-9775">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9776">Legal Compliance</p>
            <p className="text-xs" data-id="element-9777">
              These terms are displayed to customers during checkout. Ensure
              they comply with Maldivian data protection and consumer laws.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9778">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9779">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9780">
            <FileTextIcon size={18} className="text-gray-600" data-id="element-9781" />
            Terms & Conditions
          </h3>
        </div>
        <div className="p-6" data-id="element-9782">
          <FormField label="Terms Text" description="Displayed on payment pages and checkout" data-id="element-9783">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.termsText} onChange={e => setTermsSettings({
            ...termsSettings,
            termsText: e.target.value
          })} data-id="element-9784" />
          </FormField>
        </div>
      </div>

      {/* Privacy Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9785">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9786">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9787">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-9788" />
            Privacy Policy
          </h3>
        </div>
        <div className="p-6" data-id="element-9789">
          <FormField label="Privacy Policy Text" description="Information about how customer data is handled" data-id="element-9790">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent font-sans" rows={6} value={termsSettings.privacyPolicy} onChange={e => setTermsSettings({
            ...termsSettings,
            privacyPolicy: e.target.value
          })} data-id="element-9791" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9792" />
    </div>;
  const renderFields = () => <div className="space-y-6" data-id="element-9793">
      <div data-id="element-9794">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9795">
          Information Fields
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9796">
          Configure metadata fields collected at checkout
        </p>
      </div>

      {/* Informational Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-9797">
        <div className="flex items-start gap-3" data-id="element-9798">
          <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9799" />
          <div className="text-sm text-gray-700" data-id="element-9800">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9801">
              Custom Checkout Fields
            </p>
            <p className="text-xs" data-id="element-9802">
              Add custom fields to collect additional information from customers
              during checkout. This data will be attached to payment records and
              can be used for reporting or integration purposes.
            </p>
          </div>
        </div>
      </div>

      {/* Fields Editor Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9803">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9804">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9805">
            <FormInputIcon size={18} className="text-gray-600" data-id="element-9806" />
            Metadata Fields
          </h3>
        </div>
        <div className="p-6" data-id="element-9807">
          <MetadataEditor metadata={presetMetadataFields} onChange={setPresetMetadataFields} data-id="element-9808" />
        </div>
      </div>
    </div>;
  const renderNotifications = () => <div className="space-y-6" data-id="element-9809">
      <div data-id="element-9810">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9811">
          Notifications
        </h2>
        <p className="text-sm text-gray-600" data-id="element-9812">
          Manage how you receive alerts about payments and account activity
        </p>
      </div>

      {/* Notification Channels Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9813">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9814">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9815">
            <BellIcon size={18} className="text-gray-600" data-id="element-9816" />
            Notification Channels
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9817">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9818">
            <div data-id="element-9819">
              <p className="text-sm font-medium text-gray-900" data-id="element-9820">
                Email Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9821">
                Receive alerts via email
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9822">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.emailNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              emailNotifications: e.target.checked
            })} data-id="element-9823" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9824"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9825">
            <div data-id="element-9826">
              <p className="text-sm font-medium text-gray-900" data-id="element-9827">
                SMS Notifications
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9828">
                Receive alerts via text message
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9829">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.smsNotifications} onChange={e => setNotificationSettings({
              ...notificationSettings,
              smsNotifications: e.target.checked
            })} data-id="element-9830" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9831"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Event Notifications Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9832">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9833">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9834">
            <AlertCircleIcon size={18} className="text-gray-600" data-id="element-9835" />
            Event Notifications
          </h3>
        </div>
        <div className="divide-y divide-gray-200" data-id="element-9836">
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9837">
            <div data-id="element-9838">
              <p className="text-sm font-medium text-gray-900" data-id="element-9839">
                Payment Received
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9840">
                When a customer completes payment
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9841">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentReceived} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentReceived: e.target.checked
            })} data-id="element-9842" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9843"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9844">
            <div data-id="element-9845">
              <p className="text-sm font-medium text-gray-900" data-id="element-9846">
                Payment Failed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9847">
                When a payment attempt fails
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9848">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.paymentFailed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              paymentFailed: e.target.checked
            })} data-id="element-9849" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9850"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9851">
            <div data-id="element-9852">
              <p className="text-sm font-medium text-gray-900" data-id="element-9853">
                Refund Processed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9854">
                When a refund is completed
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9855">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.refundProcessed} onChange={e => setNotificationSettings({
              ...notificationSettings,
              refundProcessed: e.target.checked
            })} data-id="element-9856" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9857"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9858">
            <div data-id="element-9859">
              <p className="text-sm font-medium text-gray-900" data-id="element-9860">
                Dispute Opened
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9861">
                When a customer files a dispute
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9862">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.disputeOpened} onChange={e => setNotificationSettings({
              ...notificationSettings,
              disputeOpened: e.target.checked
            })} data-id="element-9863" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9864"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9865">
            <div data-id="element-9866">
              <p className="text-sm font-medium text-gray-900" data-id="element-9867">
                Settlement Completed
              </p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9868">
                When funds are transferred to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9869">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.settlementCompleted} onChange={e => setNotificationSettings({
              ...notificationSettings,
              settlementCompleted: e.target.checked
            })} data-id="element-9870" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9871"></div>
            </label>
          </div>
          <div className="flex items-center justify-between px-6 py-4 hover:bg-gray-50 transition-colors" data-id="element-9872">
            <div data-id="element-9873">
              <p className="text-sm font-medium text-gray-900" data-id="element-9874">Low Balance</p>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-9875">
                When your account balance is low
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9876">
              <input type="checkbox" className="sr-only peer" checked={notificationSettings.lowBalance} onChange={e => setNotificationSettings({
              ...notificationSettings,
              lowBalance: e.target.checked
            })} data-id="element-9877" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9878"></div>
            </label>
          </div>
        </div>
      </div>
    </div>;
  const renderSecurity = () => <div className="space-y-6" data-id="element-9879">
      <div data-id="element-9880">
        <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9881">Security</h2>
        <p className="text-sm text-gray-600" data-id="element-9882">
          Manage security settings and access controls for your account
        </p>
      </div>

      {/* Security Status Banner */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4" data-id="element-9883">
        <div className="flex items-start gap-3" data-id="element-9884">
          <CheckCircleIcon size={20} className="text-green-600 flex-shrink-0 mt-0.5" data-id="element-9885" />
          <div className="text-sm text-gray-700" data-id="element-9886">
            <p className="font-medium text-gray-900 mb-1" data-id="element-9887">
              Security Status: Protected
            </p>
            <p className="text-xs" data-id="element-9888">
              Your account is secured with two-factor authentication and session
              management. Keep these settings enabled for maximum protection.
            </p>
          </div>
        </div>
      </div>

      {/* Authentication Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9889">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9890">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9891">
            <LockIcon size={18} className="text-gray-600" data-id="element-9892" />
            Authentication & Access
          </h3>
        </div>
        <div className="p-6 space-y-5" data-id="element-9893">
          <div className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-4" data-id="element-9894">
            <div data-id="element-9895">
              <p className="text-sm font-medium text-gray-900 flex items-center gap-2" data-id="element-9896">
                Two-Factor Authentication
                {securitySettings.twoFactorEnabled && <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9897">
                    Enabled
                  </span>}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-9898">
                Adds an extra layer of security to your account
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" data-id="element-9899">
              <input type="checkbox" className="sr-only peer" checked={securitySettings.twoFactorEnabled} onChange={e => setSecuritySettings({
              ...securitySettings,
              twoFactorEnabled: e.target.checked
            })} data-id="element-9900" />
              <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9901"></div>
            </label>
          </div>

          <FormField label="Session Timeout (minutes)" description="Automatically log out after this period of inactivity" data-id="element-9902">
            <input type="number" className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" value={securitySettings.sessionTimeout} onChange={e => setSecuritySettings({
            ...securitySettings,
            sessionTimeout: parseInt(e.target.value)
          })} data-id="element-9903" />
          </FormField>
        </div>
      </div>

      {/* IP Whitelist Card */}
      <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9904">
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9905">
          <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9906">
            <ShieldIcon size={18} className="text-gray-600" data-id="element-9907" />
            IP Whitelist
          </h3>
        </div>
        <div className="p-6" data-id="element-9908">
          <FormField label="Allowed IP Addresses" description="Restrict API access to specific IP addresses (one per line). Leave empty to allow all IPs." data-id="element-9909">
            <textarea className="block w-full px-3 py-2 border border-gray-300 rounded-md font-mono text-sm focus:ring-2 focus:ring-[#E2231A] focus:border-transparent" rows={4} value={securitySettings.ipWhitelist} onChange={e => setSecuritySettings({
            ...securitySettings,
            ipWhitelist: e.target.value
          })} placeholder="192.168.1.1&#10;10.0.0.1" data-id="element-9910" />
          </FormField>
        </div>
      </div>

      <SaveButton data-id="element-9911" />
    </div>;
  const renderPayoutSettings = () => {
    const isMinBalanceInvalid = minBalanceEnabled && (!minBalanceAmount || Number(minBalanceAmount) <= 0);
    let pendingPayoutUpdate: {
      bank?: string;
      maskedAccount?: string;
      submittedAt?: string;
    } | null = null;
    try {
      const raw = typeof window !== 'undefined' ? sessionStorage.getItem('pendingPayoutUpdate') : null;
      if (raw) pendingPayoutUpdate = JSON.parse(raw);
    } catch {
      pendingPayoutUpdate = null;
    }
    return <div className="space-y-6" data-id="element-9912">
        <div data-id="element-9913">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-9914">
            Payout Settings
          </h2>
          <p className="text-sm text-gray-600" data-id="element-9915">
            Configure bank accounts, minimum balance, and payout preferences
          </p>
        </div>

        {/* Settlement Currencies Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9916">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9917">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9918">
              <BuildingIcon size={18} className="text-gray-600" data-id="element-9919" />
              Settlement Currencies & Bank Accounts
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-9920">
              Configure bank accounts for receiving payouts in different
              currencies
            </p>
          </div>

          <div className="overflow-x-auto" data-id="element-9921">
            <table className="w-full text-left" data-id="element-9922">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-9923">
                <tr data-id="element-9924">
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9925">
                    Currency
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9926">
                    Payout Bank Account
                  </th>
                  <th className="px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-9927">
                    <div className="flex items-center gap-1.5" data-id="element-9928">
                      Payout Fee
                      <InfoIcon size={14} className="text-gray-400" data-id="element-9929" />
                    </div>
                  </th>
                  <th className="px-6 py-3" data-id="element-9930"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-9931">
                <tr className="hover:bg-gray-50 transition-colors" data-id="element-9932">
                  <td className="px-6 py-4" data-id="element-9933">
                    <div className="flex items-center gap-2" data-id="element-9934">
                      <span className="text-sm font-medium text-gray-900" data-id="element-9935">
                        MVR
                      </span>
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-9936">
                        Default
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9937">
                    <div className="flex flex-col gap-1.5" data-id="element-9938">
                      <div className="flex items-center gap-3" data-id="element-9939">
                        <div className="w-8 h-8 rounded bg-gray-100 border border-gray-200 flex items-center justify-center flex-shrink-0" data-id="element-9940">
                          <BuildingIcon size={16} className="text-gray-500" data-id="element-9941" />
                        </div>
                        <div className="flex items-center gap-2 flex-wrap" data-id="element-9942">
                          <span className="text-sm text-gray-900" data-id="element-9943">
                            Bank of Maldives
                          </span>
                          <span className="text-sm text-gray-500 font-mono" data-id="element-9944">
                            ••••5678
                          </span>
                        </div>
                      </div>
                      {pendingPayoutUpdate && <div className="ml-11 flex items-center gap-2 flex-wrap" data-id="element-9945">
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-800 border border-amber-200" data-id="element-9946">
                            <ClockIcon size={12} data-id="element-9947" />
                            Pending Bank Review
                          </span>
                          <span className="text-xs text-gray-600" data-id="element-9948">
                            New: {pendingPayoutUpdate.bank}{' '}
                            <span className="font-mono" data-id="element-9949">
                              {pendingPayoutUpdate.maskedAccount}
                            </span>
                          </span>
                        </div>}
                    </div>
                  </td>
                  <td className="px-6 py-4" data-id="element-9950">
                    <span className="text-sm text-gray-600" data-id="element-9951">Free</span>
                  </td>
                  <td className="px-6 py-4 text-right" data-id="element-9952">
                    <div className="flex items-center justify-end gap-2" data-id="element-9953">
                      <button onClick={() => navigate('/settings/payout-account/edit')} disabled={!!pendingPayoutUpdate} title={pendingPayoutUpdate ? 'An update is pending bank review' : 'Edit payout account'} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-9954">
                        Edit
                      </button>
                      <button aria-label="More options" className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md transition-colors" data-id="element-9955">
                        <MoreHorizontalIcon size={18} data-id="element-9956" />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50" data-id="element-9957">
            <button className="text-sm font-medium text-blue-600 hover:text-blue-700 transition-colors" data-id="element-9958">
              + Add a settlement currency
            </button>
          </div>
        </div>

        {/* Minimum Balance Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9959">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9960">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9961">
              <DollarSignIcon size={18} className="text-gray-600" data-id="element-9962" />
              Minimum Balance
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-9963">
              Keep a minimum amount in your account to cover refunds and
              chargebacks
            </p>
          </div>

          <div className="p-6" data-id="element-9964">
            <div className="flex items-start gap-4" data-id="element-9965">
              <label className="relative inline-flex items-center cursor-pointer mt-0.5" data-id="element-9966">
                <input type="checkbox" className="sr-only peer" checked={minBalanceEnabled} onChange={e => setMinBalanceEnabled(e.target.checked)} data-id="element-9967" />
                <div className="w-11 h-6 bg-gray-200 peer-checked:bg-[#E2231A] rounded-full peer relative after:absolute after:top-0.5 after:left-0.5 after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-5" data-id="element-9968"></div>
              </label>

              <div className="flex-1" data-id="element-9969">
                <p className="text-sm font-medium text-gray-900" data-id="element-9970">
                  Keep a minimum amount in your account
                </p>
                <p className="text-sm text-gray-500 mt-0.5 mb-4" data-id="element-9971">
                  We'll adjust your payouts so you always maintain this balance
                </p>

                {minBalanceEnabled && <div className="mb-4" data-id="element-9972">
                    <div className="relative max-w-xs" data-id="element-9973">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-9974">
                        <span className="text-gray-500 text-sm" data-id="element-9975">MVR</span>
                      </div>
                      <input type="number" value={minBalanceAmount} onChange={e => setMinBalanceAmount(e.target.value)} className={`block w-full pl-14 pr-3 py-2 text-sm rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent ${isMinBalanceInvalid ? 'border-red-300 text-red-900' : 'border-gray-300'}`} placeholder="0.00" data-id="element-9976" />
                    </div>
                    {isMinBalanceInvalid && <p className="mt-2 text-sm text-red-600 flex items-center gap-1.5" data-id="element-9977">
                        <InfoIcon size={14} data-id="element-9978" />
                        Amount must be greater than 0
                      </p>}
                  </div>}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-9979">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-9980">
              Cancel
            </button>
            <button disabled={isMinBalanceInvalid} className={`px-4 py-2 text-sm font-medium text-white rounded-md transition-colors ${isMinBalanceInvalid ? 'bg-gray-400 cursor-not-allowed' : 'bg-[#E2231A] hover:bg-[#EB4A42] text-white'}`} data-id="element-9981">
              Save
            </button>
          </div>
        </div>

        {/* Statement Descriptor Card */}
        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-9982">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-9983">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-9984">
              <FileTextIcon size={18} className="text-gray-600" data-id="element-9985" />
              Payout Statement Descriptor
            </h3>
            <p className="text-sm text-gray-500 mt-1" data-id="element-9986">
              Customize how payouts appear on your bank statement
            </p>
          </div>

          <div className="p-6 space-y-6" data-id="element-9987">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3" data-id="element-9988">
              <InfoIcon size={20} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-9989" />
              <p className="text-sm text-gray-700 leading-relaxed" data-id="element-9990">
                Set a custom statement descriptor to help identify payouts in
                your bank account. This will appear on your bank statement for
                all payouts.
              </p>
            </div>

            <FormField label="Statement Descriptor" description="This text will appear on your bank statement" data-id="element-9991">
              <input type="text" value={payoutDescriptor} onChange={e => setPayoutDescriptor(e.target.value)} placeholder={`Example - ${companyInfo.businessName}`} className="block w-full max-w-md px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" data-id="element-9992" />
            </FormField>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3" data-id="element-9993">
            <button className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors" data-id="element-9994">
              Cancel
            </button>
            <button className="px-4 py-2 text-sm font-medium bg-[#E2231A] hover:bg-[#EB4A42] text-white rounded-md transition-colors" data-id="element-9995">
              Save
            </button>
          </div>
        </div>
      </div>;
  };
  // MDR row component
  const MdrRow = ({
    type,
    tier,
    range,
    ic,
    sf,
    am,
    total,
    color
  }: any) => <tr className={color} data-id="element-9996">
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-9997">{type}</td>
      <td className="px-4 py-2 text-sm text-gray-900" data-id="element-9998">{tier}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-9999">{range}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10000">{ic}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10001">{sf}</td>
      <td className="px-4 py-2 text-sm text-gray-600" data-id="element-10002">{am}</td>
      <td className="px-4 py-2 text-sm font-medium text-gray-900" data-id="element-10003">{total}</td>
    </tr>;
  const FixedFee = ({
    label,
    sublabel,
    amount
  }: {
    label: string;
    sublabel: string;
    amount: number | string;
  }) => <div className="flex items-center justify-between" data-id="element-10004">
      <div data-id="element-10005">
        <p className="text-sm text-gray-900 font-medium" data-id="element-10006">{label}</p>
        <p className="text-xs text-gray-500" data-id="element-10007">{sublabel}</p>
      </div>
      <span className="text-sm font-semibold text-gray-900" data-id="element-10008">
        {typeof amount === 'number' ? formatCurrencyAmount(amount, {
        currency
      }) : amount}
      </span>
    </div>;
  const renderSubscriptionPlanSettings = () => <div data-id="element-10009">
      <h2 className="text-lg font-medium text-gray-800 mb-4" data-id="element-10010">
        Subscription Plan
      </h2>
      <div className="space-y-6" data-id="element-10011">
        {/* Supported Payment Methods */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10012">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10013">
            <CreditCardIcon size={16} className="mr-2 text-blue-600" data-id="element-10014" />
            Supported Payment Methods
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10015">
            Payment methods enabled for your account with their merchant
            discount rates
          </p>

          {/* Card Payments */}
          <div className="mb-6" data-id="element-10016">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-10017">
              Card Payments
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10018">
              {/* Visa */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10019">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-10020">
                  <div className="flex items-center justify-between" data-id="element-10021">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10022">
                      Visa
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10023">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10024">
                  <div className="flex items-center justify-between" data-id="element-10025">
                    <span className="text-xs text-gray-600" data-id="element-10026">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10027">
                      0.80% - 1.30%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10028">
                    <span className="text-xs text-gray-600" data-id="element-10029">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10030">
                      1.10% - 1.70%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10031">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-10032">
                      + MVR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-10033">
                      <p className="text-xs text-gray-600" data-id="element-10034">
                        <span className="font-medium" data-id="element-10035">MVR 1-1,000:</span> 0.80%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-10036">
                        <span className="font-medium" data-id="element-10037">MVR 1,000+:</span> 1.30%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Mastercard */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10038">
                <div className="bg-orange-50 px-4 py-3 border-b border-orange-200" data-id="element-10039">
                  <div className="flex items-center justify-between" data-id="element-10040">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10041">
                      Mastercard
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10042">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10043">
                  <div className="flex items-center justify-between" data-id="element-10044">
                    <span className="text-xs text-gray-600" data-id="element-10045">Debit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10046">
                      0.85% - 1.35%
                    </span>
                  </div>
                  <div className="flex items-center justify-between" data-id="element-10047">
                    <span className="text-xs text-gray-600" data-id="element-10048">Credit Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10049">
                      1.15% - 1.75%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10050">
                    <p className="text-xs text-gray-500 mb-2" data-id="element-10051">
                      + MVR 0.50 per transaction
                    </p>
                    <div className="bg-gray-50 rounded p-2 space-y-1" data-id="element-10052">
                      <p className="text-xs text-gray-600" data-id="element-10053">
                        <span className="font-medium" data-id="element-10054">MVR 1-500:</span> 0.85%
                      </p>
                      <p className="text-xs text-gray-600" data-id="element-10055">
                        <span className="font-medium" data-id="element-10056">MVR 500+:</span> 1.35%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* American Express */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10057">
                <div className="bg-indigo-50 px-4 py-3 border-b border-indigo-200" data-id="element-10058">
                  <div className="flex items-center justify-between" data-id="element-10059">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10060">
                      American Express
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10061">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4 space-y-3" data-id="element-10062">
                  <div className="flex items-center justify-between" data-id="element-10063">
                    <span className="text-xs text-gray-600" data-id="element-10064">All Cards</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10065">
                      2.40% - 2.60%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10066">
                    <p className="text-xs text-gray-500" data-id="element-10067">
                      + MVR 0.75 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* E-Wallets & Online Banking */}
          <div data-id="element-10068">
            <h4 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-10069">
              E-Wallets & Online Banking
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-10070">
              {/* Touch 'n Go */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10071">
                <div className="bg-blue-50 px-4 py-3 border-b border-blue-200" data-id="element-10072">
                  <div className="flex items-center justify-between" data-id="element-10073">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10074">
                      Touch 'n Go
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10075">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10076">
                  <div className="flex items-center justify-between mb-2" data-id="element-10077">
                    <span className="text-xs text-gray-600" data-id="element-10078">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10079">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10080">
                    <p className="text-xs text-gray-500" data-id="element-10081">
                      + MVR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* GrabPay */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10082">
                <div className="bg-green-50 px-4 py-3 border-b border-green-200" data-id="element-10083">
                  <div className="flex items-center justify-between" data-id="element-10084">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10085">
                      GrabPay
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10086">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10087">
                  <div className="flex items-center justify-between mb-2" data-id="element-10088">
                    <span className="text-xs text-gray-600" data-id="element-10089">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10090">
                      1.50%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10091">
                    <p className="text-xs text-gray-500" data-id="element-10092">
                      + MVR 0.30 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* FPX */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10093">
                <div className="bg-purple-50 px-4 py-3 border-b border-purple-200" data-id="element-10094">
                  <div className="flex items-center justify-between" data-id="element-10095">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10096">
                      FPX
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10097">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10098">
                  <div className="flex items-center justify-between mb-2" data-id="element-10099">
                    <span className="text-xs text-gray-600" data-id="element-10100">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10101">
                      1.20%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10102">
                    <p className="text-xs text-gray-500" data-id="element-10103">
                      + MVR 0.40 per transaction
                    </p>
                  </div>
                </div>
              </div>

              {/* DuitNow QR */}
              <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10104">
                <div className="bg-amber-50 px-4 py-3 border-b border-amber-200" data-id="element-10105">
                  <div className="flex items-center justify-between" data-id="element-10106">
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10107">
                      DuitNow QR
                    </span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800" data-id="element-10108">
                      Active
                    </span>
                  </div>
                </div>
                <div className="p-4" data-id="element-10109">
                  <div className="flex items-center justify-between mb-2" data-id="element-10110">
                    <span className="text-xs text-gray-600" data-id="element-10111">MDR</span>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-10112">
                      0.80%
                    </span>
                  </div>
                  <div className="pt-2 border-t border-gray-200" data-id="element-10113">
                    <p className="text-xs text-gray-500" data-id="element-10114">
                      + MVR 0.25 per transaction
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-10115">
            <div className="flex items-start gap-2" data-id="element-10116">
              <InfoIcon size={16} className="text-blue-600 flex-shrink-0 mt-0.5" data-id="element-10117" />
              <div className="text-sm text-gray-700" data-id="element-10118">
                <p className="font-medium text-gray-900 mb-1" data-id="element-10119">Pricing Notes:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-10120">
                  <li data-id="element-10121">
                    Card rates vary based on card type (debit/credit) and
                    issuing bank
                  </li>
                  <li data-id="element-10122">
                    Lower rates apply to Bank of Maldives-issued cards (on-us
                    transactions)
                  </li>
                  <li data-id="element-10123">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Transaction Fees & Additional Charges */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10124">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10125">
            <TagIcon size={16} className="mr-2 text-blue-600" data-id="element-10126" />
            Transaction Fees & Additional Charges
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-10127">
            Fixed fees and additional charges per transaction or event
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4" data-id="element-10128">
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10129">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10130">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-10131">
                  Card Transaction Fees
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-10132">
                <FixedFee label="Visa/Mastercard" sublabel="Per successful transaction" amount={0.5} data-id="element-10133" />
                <FixedFee label="American Express" sublabel="Per successful transaction" amount={0.75} data-id="element-10134" />
                <FixedFee label="Authorization Fee" sublabel="Per authorization attempt" amount={0.2} data-id="element-10135" />
              </div>
            </div>
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10136">
              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10137">
                <h4 className="text-sm font-semibold text-gray-900" data-id="element-10138">
                  E-Wallet & Online Banking
                </h4>
              </div>
              <div className="p-4 space-y-3" data-id="element-10139">
                <FixedFee label="Touch 'n Go / GrabPay" sublabel="Per successful transaction" amount={0.3} data-id="element-10140" />
                <FixedFee label="FPX (Online Banking)" sublabel="Per successful transaction" amount={0.4} data-id="element-10141" />
                <FixedFee label="DuitNow QR" sublabel="Per successful transaction" amount={0.25} data-id="element-10142" />
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10143">
            <div className="bg-gray-50 px-4 py-2 border-b border-gray-200" data-id="element-10144">
              <h4 className="text-sm font-semibold text-gray-900" data-id="element-10145">
                Additional Service Fees
              </h4>
            </div>
            <table className="w-full text-left" data-id="element-10146">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10147">
                <tr data-id="element-10148">
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-10149">
                    Service
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500" data-id="element-10150">
                    Description
                  </th>
                  <th className="px-4 py-2 text-xs font-medium text-gray-500 text-right" data-id="element-10151">
                    Fee
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10152">
                <tr data-id="element-10153">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10154">
                    Refund Processing
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10155">
                    Per refund transaction
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-10156">
                    {formatCurrencyAmount(1.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-10157">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10158">
                    Chargeback Fee
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10159">
                    Per chargeback filed
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-10160">
                    {formatCurrencyAmount(50.0, {
                    currency
                  })}
                  </td>
                </tr>
                <tr data-id="element-10161">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900" data-id="element-10162">
                    Chargeback Reversal
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600" data-id="element-10163">
                    When chargeback is won
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-green-600 text-right" data-id="element-10164">{`-${formatCurrencyAmount(50.0, {
                    currency
                  })}`}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-10165">
            <div className="flex items-start gap-2" data-id="element-10166">
              <InfoIcon size={16} className="text-amber-600 flex-shrink-0 mt-0.5" data-id="element-10167" />
              <div className="text-sm text-gray-700" data-id="element-10168">
                <p className="font-medium text-gray-900 mb-1" data-id="element-10169">Fee Structure:</p>
                <ul className="list-disc list-inside space-y-1 text-xs" data-id="element-10170">
                  <li data-id="element-10171">
                    <span className="font-medium" data-id="element-10172">Total cost</span> =
                    (Transaction Amount × MDR%) + Fixed Fee
                  </li>
                  <li data-id="element-10173">
                    Example: MVR 100 Visa on-us = (MVR 100 × 1.10%) + MVR 0.50 =
                    MVR 1.60
                  </li>
                  <li data-id="element-10174">All fees subject to applicable taxes (SST 6%)</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Subscription Fees */}
        <div className="border-b border-gray-200 pb-6" data-id="element-10175">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center" data-id="element-10176">
            <DollarSignIcon size={16} className="mr-2 text-blue-600" data-id="element-10177" />
            Subscription Fees
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-10178">
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-10179">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10180">
                Annual Rental
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-10181">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10182">Billed annually</p>
            </div>
            <div className="border border-gray-200 rounded-lg p-4" data-id="element-10183">
              <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10184">
                Monthly Equivalent
              </p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-10185">
                {formatCurrencyAmount(200, {
                currency
              })}
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10186">
                MVR 2,400 / 12 months
              </p>
            </div>
          </div>
        </div>

        {/* Last Payment */}
        <div data-id="element-10187">
          <h3 className="text-md font-medium text-gray-700 mb-3 flex items-center gap-2" data-id="element-10188">
            <ClockIcon size={16} className="mr-2 text-blue-600" data-id="element-10189" />
            Last Subscription Payment
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-10190">
            <div data-id="element-10191">
              <p className="text-sm font-medium text-gray-900" data-id="element-10192">
                Annual Subscription Renewal
              </p>
              <p className="text-xs text-gray-500 mt-1" data-id="element-10193">
                Payment Date: January 15, 2026
              </p>
            </div>
            <div className="text-right" data-id="element-10194">
              <p className="text-lg font-bold text-gray-900" data-id="element-10195">
                {formatCurrencyAmount(2400, {
                currency
              })}
              </p>
              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800 mt-1" data-id="element-10196">
                Paid
              </span>
            </div>
          </div>
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-lg p-3" data-id="element-10197">
            <p className="text-xs text-gray-600" data-id="element-10198">
              <span className="font-medium" data-id="element-10199">Next renewal:</span> January 15,
              2027
            </p>
          </div>
        </div>
      </div>
    </div>;
  const renderActivityLogs = () => {
    const filteredLogs = activityLogs.filter(log => {
      if (logFilters.search && !log.description.toLowerCase().includes(logFilters.search.toLowerCase()) && !log.user.name.toLowerCase().includes(logFilters.search.toLowerCase())) return false;
      if (logFilters.user !== 'all' && log.user.id !== logFilters.user) return false;
      if (logFilters.section !== 'all' && log.section !== logFilters.section) return false;
      if (logFilters.dateRange !== 'all') {
        const logDate = new Date(log.timestamp);
        const now = new Date();
        const daysDiff = Math.floor((now.getTime() - logDate.getTime()) / (1000 * 60 * 60 * 24));
        if (logFilters.dateRange === '24h' && daysDiff > 1) return false;
        if (logFilters.dateRange === '7days' && daysDiff > 7) return false;
        if (logFilters.dateRange === '30days' && daysDiff > 30) return false;
      }
      return true;
    });
    const totalPages = Math.ceil(filteredLogs.length / logsPerPage);
    const paginatedLogs = filteredLogs.slice((currentLogPage - 1) * logsPerPage, currentLogPage * logsPerPage);
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10200">
        <div data-id="element-10201">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10202">
            Activity Logs
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10203">
            View and track all user activities and system events
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10204">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10205">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10206">
              <SearchIcon size={18} className="text-gray-600" data-id="element-10207" />
              Filters
            </h3>
          </div>
          <div className="p-6 space-y-4" data-id="element-10208">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-10209">
              <div data-id="element-10210">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10211">
                  Search
                </label>
                <input type="text" placeholder="Search logs..." className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.search} onChange={e => setLogFilters({
                ...logFilters,
                search: e.target.value
              })} data-id="element-10212" />
              </div>
              <div data-id="element-10213">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10214">
                  Date Range
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.dateRange} onChange={e => setLogFilters({
                ...logFilters,
                dateRange: e.target.value
              })} data-id="element-10215">
                  <option value="all" data-id="element-10216">All Time</option>
                  <option value="24h" data-id="element-10217">Last 24 Hours</option>
                  <option value="7days" data-id="element-10218">Last 7 Days</option>
                  <option value="30days" data-id="element-10219">Last 30 Days</option>
                </select>
              </div>
              <div data-id="element-10220">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10221">
                  User
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.user} onChange={e => setLogFilters({
                ...logFilters,
                user: e.target.value
              })} data-id="element-10222">
                  <option value="all" data-id="element-10223">All Users</option>
                  {users.map(u => <option key={u.id} value={u.id} data-id="element-10224">
                      {u.name}
                    </option>)}
                </select>
              </div>
              <div data-id="element-10225">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10226">
                  Section
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logFilters.section} onChange={e => setLogFilters({
                ...logFilters,
                section: e.target.value
              })} data-id="element-10227">
                  <option value="all" data-id="element-10228">All Sections</option>
                  <option value="Payments" data-id="element-10229">Payments</option>
                  <option value="Customers" data-id="element-10230">Customers</option>
                  <option value="Invoices" data-id="element-10231">Invoices</option>
                  <option value="Bills" data-id="element-10232">Bills</option>
                  <option value="Refunds" data-id="element-10233">Refunds</option>
                  <option value="Users" data-id="element-10234">Users</option>
                  <option value="Settings" data-id="element-10235">Settings</option>
                  <option value="Security" data-id="element-10236">Security</option>
                </select>
              </div>
              <div data-id="element-10237">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10238">
                  Per Page
                </label>
                <select className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#E2231A] focus:border-transparent text-sm" value={logsPerPage} onChange={e => {
                setLogsPerPage(Number(e.target.value));
                setCurrentLogPage(1);
              }} data-id="element-10239">
                  <option value="25" data-id="element-10240">25</option>
                  <option value="50" data-id="element-10241">50</option>
                  <option value="100" data-id="element-10242">100</option>
                </select>
              </div>
            </div>
            <div className="flex items-center justify-between pt-2" data-id="element-10243">
              <p className="text-sm text-gray-600" data-id="element-10244">
                Showing {paginatedLogs.length} of {filteredLogs.length} logs
              </p>
            </div>
          </div>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10245">
          <div className="overflow-x-auto" data-id="element-10246">
            <table className="w-full" data-id="element-10247">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10248">
                <tr data-id="element-10249">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10250">
                    Timestamp
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10251">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10252">
                    Action
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10253">
                    Section
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10254">
                    Object ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10255">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10256">
                {paginatedLogs.map(log => <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-id="element-10257">
                    <td className="px-6 py-4 text-sm text-gray-900" title={new Date(log.timestamp).toLocaleString()} data-id="element-10258">
                      {getRelativeTime(log.timestamp)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10259">
                      <div className="flex items-center gap-2" data-id="element-10260">
                        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-xs font-semibold" data-id="element-10261">
                          {log.user.name.split(' ').map(n => n[0]).join('')}
                        </div>
                        <div data-id="element-10262">
                          <p className="text-sm font-medium text-gray-900" data-id="element-10263">
                            {log.user.name}
                          </p>
                          <p className="text-xs text-gray-500" data-id="element-10264">
                            {log.user.role}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-10265">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800" data-id="element-10266">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" data-id="element-10267">
                      {log.section}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10268">
                      {log.objectId}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 font-mono" data-id="element-10269">
                      {log.ipAddress}
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between bg-gray-50" data-id="element-10270">
              <button onClick={() => setCurrentLogPage(Math.max(1, currentLogPage - 1))} disabled={currentLogPage === 1} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10271">
                Previous
              </button>
              <span className="text-sm text-gray-600" data-id="element-10272">
                Page {currentLogPage} of {totalPages}
              </span>
              <button onClick={() => setCurrentLogPage(Math.min(totalPages, currentLogPage + 1))} disabled={currentLogPage === totalPages} className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-10273">
                Next
              </button>
            </div>}
        </div>
      </div>;
  };
  const renderRequests = () => {
    const getStatusColor = (status: string) => {
      switch (status) {
        case 'Closed':
          return 'bg-gray-100 text-gray-800';
        case 'Pending':
          return 'bg-amber-100 text-amber-800';
        case 'In Progress':
          return 'bg-blue-100 text-blue-800';
        default:
          return 'bg-gray-100 text-gray-800';
      }
    };
    const getRelativeTime = (timestamp: string) => {
      const now = new Date();
      const then = new Date(timestamp);
      const seconds = Math.floor((now.getTime() - then.getTime()) / 1000);
      if (seconds < 60) return `${seconds}s ago`;
      const minutes = Math.floor(seconds / 60);
      if (minutes < 60) return `${minutes}m ago`;
      const hours = Math.floor(minutes / 60);
      if (hours < 24) return `${hours}h ago`;
      const days = Math.floor(hours / 24);
      return `${days}d ago`;
    };
    return <div className="space-y-6" data-id="element-10274">
        <div data-id="element-10275">
          <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-10276">
            Merchant Requests
          </h2>
          <p className="text-sm text-gray-600" data-id="element-10277">
            View and track requests for payment method activations and custom
            user roles
          </p>
        </div>

        <div className="border border-gray-200 rounded-lg overflow-hidden" data-id="element-10278">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200" data-id="element-10279">
            <h3 className="text-md font-semibold text-gray-900 flex items-center gap-2" data-id="element-10280">
              <ClipboardListIcon size={18} className="text-gray-600" data-id="element-10281" />
              All Requests ({merchantRequests.length})
            </h3>
          </div>
          <div className="overflow-x-auto" data-id="element-10282">
            <table className="w-full" data-id="element-10283">
              <thead className="bg-gray-50 border-b border-gray-200" data-id="element-10284">
                <tr data-id="element-10285">
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10286">
                    Request ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10287">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10288">
                    Description
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10289">
                    Requested By
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10290">
                    Requested At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-10291">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white" data-id="element-10292">
                {merchantRequests.map(request => <tr key={request.id} className="hover:bg-gray-50 transition-colors" data-id="element-10293">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 font-mono" data-id="element-10294">
                      {request.id}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10295">
                      {request.type}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs" data-id="element-10296">
                      {request.description}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900" data-id="element-10297">
                      {request.requestedBy}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600" title={new Date(request.requestedAt).toLocaleString()} data-id="element-10298">
                      {getRelativeTime(request.requestedAt)}
                    </td>
                    <td className="px-6 py-4" data-id="element-10299">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(request.status)}`} data-id="element-10300">
                        {request.status}
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-10301">
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10302">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10303">
              Total Requests
            </p>
            <p className="text-2xl font-bold text-gray-900" data-id="element-10304">
              {merchantRequests.length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10305">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10306">
              Pending
            </p>
            <p className="text-2xl font-bold text-amber-600" data-id="element-10307">
              {merchantRequests.filter(r => r.status === 'Pending').length}
            </p>
          </div>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10308">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-1" data-id="element-10309">
              Closed
            </p>
            <p className="text-2xl font-bold text-gray-600" data-id="element-10310">
              {merchantRequests.filter(r => r.status === 'Closed').length}
            </p>
          </div>
        </div>
      </div>;
  };
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'company':
        return renderCompanySettings();
      case 'branding':
        return renderBrandingSettings();
      case 'general':
        return renderGeneralSettings();
      case 'receipt':
        return renderReceiptSettings();
      case 'payment-providers':
        return renderPaymentProviders();
      case 'users':
        return renderUsers();
      case 'payout':
        return renderPayoutSettings();
      case 'fields':
        return renderFields();
      case 'notifications':
        return renderNotifications();
      case 'security':
        return renderSecurity();
      case 'subscription-plan':
        return renderSubscriptionPlanSettings();
      default:
        return null;
    }
  };
  return <div className="max-w-7xl mx-auto py-4 sm:py-8 px-4" data-id="element-10311">
      <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 sm:mb-8" data-id="element-10312">
        Settings
      </h1>
      <div className="flex flex-col lg:flex-row gap-4 sm:gap-6" data-id="element-10313">
        <nav className="lg:w-64 flex-shrink-0" data-id="element-10314">
          <div className="lg:hidden mb-4" data-id="element-10315">
            <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 scrollbar-hide" data-id="element-10316">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${isActive ? 'bg-[#E2231A] text-white' : 'text-gray-600 bg-gray-100 hover:bg-gray-200'}`} data-id="element-10317">
                    <Icon size={16} data-id="element-10318" />
                    <span data-id="element-10319">{tab.label}</span>
                  </button>;
            })}
            </div>
          </div>
          <div className="hidden lg:block" data-id="element-10320">
            <div className="space-y-6" data-id="element-10321">
              {categories.map(category => <div key={category.id} data-id="element-10322">
                  <h3 className="px-3 text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2" data-id="element-10323">
                    {category.label}
                  </h3>
                  <ul className="space-y-1" data-id="element-10324">
                    {tabs.filter(tab => tab.category === category.id).map(tab => {
                  const Icon = tab.icon;
                  const isActive = activeTab === tab.id;
                  return <li key={tab.id} data-id="element-10325">
                            <button onClick={() => setActiveTab(tab.id)} className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md transition-colors ${isActive ? 'bg-[#E2231A] text-white' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-10326">
                              <Icon size={16} data-id="element-10327" />
                              <span data-id="element-10328">{tab.label}</span>
                            </button>
                          </li>;
                })}
                  </ul>
                </div>)}
            </div>
          </div>
        </nav>
        <div className="flex-1 bg-white border border-gray-200 rounded-lg p-4 sm:p-6 overflow-x-auto" data-id="element-10329">
          {renderActiveTab()}
        </div>
      </div>
    </div>;
}